<template>
	<div class="card">
		<div class="card-body">
			<!-- File Upload Section -->
			<div class="row">
				<!-- Order File Upload -->
				<div class="col-md-6">
					<div class="form-group">
						<label class="form-label font-weight-bold">
							<i class="material-icons mr-1" style="font-size: 18px; vertical-align: middle;">description</i>
							File Order (Excel)
						</label>
						<div class="custom-file">
							<input
								type="file"
								class="custom-file-input"
								id="orderFile"
								:class="{ 'is-invalid': orderFileError }"
								accept=".xlsx,.xls,.csv"
								@change="handleOrderFileUpload"
								:disabled="isProcessing"
							>
							<label class="custom-file-label" for="orderFile">
								{{ selectedFile ? selectedFile.name : 'Pilih file order...' }}
							</label>
						</div>
						<small class="form-text text-muted">
							.xlsx, .xls, .csv (Max 5MB)
						</small>
						<div class="mt-2">
							<a class="btn btn-light btn-sm" href="https://cdn.ostock.id/uploads/2026/01/Form_Import_Order_Ostock_Sample.xlsx">
								<i class="material-icons">file_download</i> Download Format Import Order
							</a>
						</div>
						<div v-if="orderFileError" class="text-danger small mt-1">
							{{ orderFileError }}
						</div>
					</div>
				</div>

				<!-- Shipping Label Upload -->
				<div class="col-md-6">
					<div class="form-group">
						<label class="form-label font-weight-bold">
							<i class="material-icons mr-1" style="font-size: 18px; vertical-align: middle;">local_shipping</i>
							Shipping Label (PDF)
						</label>
						<div class="custom-file">
							<input
								type="file"
								class="custom-file-input"
								id="shippingLabelFile"
								:class="{ 'is-invalid': shippingLabelError }"
								accept=".pdf"
								@change="handleShippingLabelUpload"
								:disabled="isProcessing"
							>
							<label class="custom-file-label" for="shippingLabelFile">
								{{ shippingLabelFile ? shippingLabelFile.name : 'Pilih file shipping label...' }}
							</label>
						</div>
						<small class="form-text text-muted">
							.pdf (Max 10MB)
						</small>
						<div v-if="shippingLabelError" class="text-danger small mt-1">
							{{ shippingLabelError }}
						</div>
					</div>
				</div>
			</div>

			<!-- Supplier Selection -->
			<hr/>
			<div class="form-group">
				<div class="d-flex justify-content-between align-items-center mb-3">
					<label class="form-label font-weight-bold mb-0">
						<i class="material-icons mr-1" style="font-size: 18px; vertical-align: middle;">business</i>
						Pilih Supplier
					</label>
					<div class="search-supplier" style="max-width: 300px;">
						<div class="input-group">
							<div class="input-group-prepend">
								<span class="input-group-text">
									<i class="material-icons" style="font-size: 18px;">search</i>
								</span>
							</div>
							<input
								type="text"
								class="form-control bg-white"
								placeholder="Cari supplier..."
								v-model="searchQuery"
								:disabled="isProcessing"
							>
						</div>
					</div>
				</div>

				<!-- Loading Suppliers -->
				<div v-if="isLoadingSuppliers" class="text-center py-4 text-muted">
					<i class="material-icons spin mr-2">refresh</i>
					Memuat daftar supplier...
				</div>

				<!-- Supplier Cards -->
				<div v-else-if="filteredSuppliers.length > 0" class="row">
					<div
						v-for="supplier in filteredSuppliers"
						:key="supplier._id"
						class="col-md-4 mb-3"
					>
						<div
							class="supplier-card"
							:class="{ 'supplier-card--selected': selectedSupplierId === supplier._id }"
							@click="selectSupplier(supplier._id)"
							style="cursor: pointer;"
						>
							<div class="supplier-card__radio">
								<div class="custom-control custom-radio">
									<input
										:id="`supplier-${supplier._id}`"
										type="radio"
										:name="'supplier'"
										:value="supplier._id"
										:checked="selectedSupplierId === supplier._id"
										@change="selectSupplier(supplier._id)"
										:disabled="isProcessing"
										class="custom-control-input"
									>
									<label :for="`supplier-${supplier._id}`" class="custom-control-label"></label>
								</div>
							</div>
							<div class="supplier-card__content">
								<div class="supplier-card__code mb-0">
									{{ supplier.name }}
								</div>
								<div class="mb-2">
									<small>
										{{ supplier.code }}
									</small>
								</div>
								<div class="supplier-card__address">
									<i class="material-icons" style="font-size: 14px; vertical-align: middle;">location_on</i>
									{{ supplier.address }}
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Pagination -->
				<Pagination
					v-if="!isLoadingSuppliers && filteredSuppliers.length > 0"
					:current-page="supplierPagination.currentPage"
					:total-pages="supplierPagination.totalPages"
					:total-items="supplierPagination.totalSuppliers"
					:item-count="filteredSuppliers.length"
					:has-search="!!searchQuery"
					:is-loading="isLoadingSuppliers"
					use-modern-layout
					use-icons
					@page-changed="changePage"
				>
					<template #info>
						Menampilkan {{ filteredSuppliers.length }} dari {{ supplierPagination.totalSuppliers }} supplier
						<span v-if="searchQuery">(hasil pencarian)</span>
					</template>
				</Pagination>

				<!-- No Suppliers -->
				<div v-else-if="!isLoadingSuppliers && filteredSuppliers.length === 0" class="alert alert-warning">
					<div class="d-flex align-items-center">
						<i class="material-icons mr-2">warning</i>
						<div>
							<strong>Tidak ada supplier tersedia</strong>
							<p class="mb-0 small">Silakan tambahkan supplier terlebih dahulu.</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import readXlsxFile from 'read-excel-file';
import { mapGetters, mapActions } from 'vuex';
import Pagination from '@/components/Pagination.vue';

export default {
	name: 'Step1Configuration',
	components: {
		Pagination,
	},
	data() {
		return {
			isLoadingSuppliers: false,
			searchQuery: '',
			orderFileError: '',
			shippingLabelError: '',
			searchTimeout: null,
		};
	},
	computed: {
		...mapGetters('importWholesaleOrder', [
			'selectedFile',
			'shippingLabelFile',
			'selectedSupplierId',
			'suppliers',
			'supplierPagination',
			'isProcessing',
			'availableProducts',
		]),
		// Use suppliers directly as API handles filtering
		filteredSuppliers() {
			return this.suppliers;
		},
	},
	async created() {
		await this.loadSuppliers();
	},
	beforeDestroy() {
		// Clean up timeout to prevent memory leaks
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	watch: {
		searchQuery(newVal) {
			// Clear previous timeout
			if (this.searchTimeout) {
				clearTimeout(this.searchTimeout);
			}

			// Set loading state
			this.isLoadingSuppliers = true;

			// Debounce API call - always reset to page 1 when searching
			this.searchTimeout = setTimeout(async () => {
				await this.loadSuppliers({ keyword: newVal, page: 1 });
				this.isLoadingSuppliers = false;
			}, 1000);
		},
	},
	methods: {
		...mapActions('importWholesaleOrder', [
			'setSelectedFile',
			'setShippingLabelFile',
			'setSelectedSupplierId',
			'setMappingData',
			'setIsProcessing',
			'loadSuppliers',
		]),

		async handleOrderFileUpload(event) {
			const file = event.target.files[0];
			if (!file) {
				this.orderFileError = '';
				return;
			}

			// Reset error
			this.orderFileError = '';

			// Validate file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				this.orderFileError = 'Ukuran file terlalu besar. Maksimal 5MB.';
				this.$notify({
					group: 'app',
					title: 'Error',
					text: this.orderFileError,
					type: 'error',
				});
				return;
			}

			this.setSelectedFile(file);
			await this.parseFile(file);
		},

		async handleShippingLabelUpload(event) {
			const file = event.target.files[0];
			if (!file) {
				this.shippingLabelError = '';
				return;
			}

			// Reset error
			this.shippingLabelError = '';

			// Validate file type
			if (file.type !== 'application/pdf') {
				this.shippingLabelError = 'Format file harus PDF.';
				this.$notify({
					group: 'app',
					title: 'Error',
					text: this.shippingLabelError,
					type: 'error',
				});
				return;
			}

			// Validate file size (max 10MB)
			if (file.size > 10 * 1024 * 1024) {
				this.shippingLabelError = 'Ukuran file terlalu besar. Maksimal 10MB.';
				this.$notify({
					group: 'app',
					title: 'Error',
					text: this.shippingLabelError,
					type: 'error',
				});
				return;
			}

			this.setShippingLabelFile(file);

			this.$notify({
				group: 'app',
				title: 'Berhasil',
				text: 'Shipping label berhasil diupload',
				type: 'success',
			});
		},

		async parseFile(file) {
			this.setIsProcessing(true);

			try {
				console.log('Parsing file:', file.name); // eslint-disable-line no-console

				// Read the Excel file
				const rows = await readXlsxFile(file);

				if (!rows || rows.length < 2) {
					throw new Error('File tidak memiliki data yang valid');
				}

				const orderIdIndex = 0;
				const nameIndex = 2;
				const qtyIndex = 3;

				// Parse data rows (skip header row)
				const productMap = new Map(); // Group by product name for UI
				const packageMap = new Map(); // Group by orderId for packages

				for (let i = 1; i < rows.length; i++) {
					const row = rows[i];
					if (!row || row.length === 0) {
						// eslint-disable-next-line no-continue
						continue;
					}

					const orderId = row[orderIdIndex];
					const productName = row[nameIndex];
					const quantity = row[qtyIndex] || 1;

					if (!productName) {
						// eslint-disable-next-line no-continue
						continue;
					}

					const normalizedProductName = productName.toString().trim();
					const parsedQuantity = parseInt(quantity, 10) || 1;
					const normalizedOrderId = orderId ? orderId.toString().trim() : null;

					// Group products by name for UI (mappingDataPerProduct)
					if (productMap.has(normalizedProductName)) {
						const existing = productMap.get(normalizedProductName);
						existing.quantity += parsedQuantity;
					} else {
						productMap.set(normalizedProductName, {
							productName: normalizedProductName,
							quantity: parsedQuantity,
							selectedProductIds: [],
							selectedProducts: [],
							isValid: false,
						});
					}

					// Group by orderId for packages (mappingDataPerPackage)
					if (normalizedOrderId) {
						if (packageMap.has(normalizedOrderId)) {
							const existingPackage = packageMap.get(normalizedOrderId);
							existingPackage.items.push({
								productName: normalizedProductName,
								quantity: parsedQuantity,
							});
						} else {
							packageMap.set(normalizedOrderId, {
								orderId: normalizedOrderId,
								items: [{
									productName: normalizedProductName,
									quantity: parsedQuantity,
								}],
							});
						}
					}
				}

				// Convert maps to arrays
				const perProductData = Array.from(productMap.values());
				const perPackageData = Array.from(packageMap.values());

				// console.log('perPackageData', perPackageData);
				// console.log('packageMap', packageMap);

				this.setMappingData({
					perProduct: perProductData,
					perPackage: perPackageData,
				});

				// Show success message
				this.$notify({
					group: 'app',
					title: 'Berhasil',
					text: `File berhasil diproses. ${perPackageData.length} order ditemukan.`,
					type: 'success',
				});
			} catch (error) {
				console.error('Error parsing file:', error);
				this.$notify({
					group: 'app',
					title: 'Error',
					text: error.message || 'Gagal memproses file order',
					type: 'error',
				});
			} finally {
				this.setIsProcessing(false);
			}
		},

		selectSupplier(supplierId) {
			this.setSelectedSupplierId(supplierId);
		},

		async changePage(page) {
			this.isLoadingSuppliers = true;
			try {
				await this.loadSuppliers({ code: this.searchQuery, page });
			} finally {
				this.isLoadingSuppliers = false;
			}
		},

		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes';
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return `${Math.round((bytes / (k ** i)) * 100) / 100} ${sizes[i]}`;
		},
	},
};
</script>

<style scoped>
.custom-file-input:lang(en) ~ .custom-file-label::after {
	content: "Browse";
}

/* Border coloring based on file state */
.custom-file-input:checked ~ .custom-file-label {
	border-color: #28a745;
	background-color: #f0fff4;
}

.custom-file-input.is-invalid ~ .custom-file-label {
	border-color: #dc3545;
	background-color: #fff5f5;
}

/* Supplier Card Styles */
.supplier-card {
	border: 2px solid #dee2e6;
	border-radius: 12px;
	padding: 1rem;
	transition: all 0.2s ease-in-out;
	position: relative;
	background-color: #fff;
	height: 100%;
}

.supplier-card:hover {
	border-color: #f28901;
	box-shadow: 0 4px 12px rgba(242, 137, 1, 0.2);
	transform: translateY(-2px);
}

.supplier-card--selected {
	border-color: #276dc7;
	background-color: #f0f7ff;
	box-shadow: 0 4px 12px rgba(39, 109, 199, 0.25);
}

.supplier-card--selected:hover {
	border-color: #276dc7;
}

.supplier-card__radio {
	position: absolute;
	top: 0.75rem;
	right: 0.75rem;
}

.supplier-card__content {
	padding-right: 2rem;
}

.supplier-card__code {
	font-weight: 700;
	font-size: 1rem;
	color: #212529;
	margin-bottom: 0.5rem;
}

.supplier-card__address {
	font-size: 0.875rem;
	color: #495057;
	line-height: 1.4;
	display: flex;
	align-items: flex-start;
	gap: 0.25rem;
}

/* Spinning icon animation */
.material-icons.spin {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.custom-control-input:disabled ~ .custom-control-label {
	color: #6c757d;
}

/* Search supplier styling */
.search-supplier .input-group-text {
	background-color: #f8f9fa;
	border-right: none;
}

.search-supplier .form-control {
	border-left: none;
}

.search-supplier .input-group-text + .form-control:focus {
	border-color: #80bdff;
	box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
</style>
